import { checkLogoFile } from "./TokenVisibility";
import { useRef, useState } from "react";
import { Palette, ImagePlus, Trash2, Loader2, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { api, errMsg, BACKEND } from "../lib/api";

const DEFAULT = { frame: "#39ff14", accent: "#39ff14" };
const PRESETS = ["#39ff14", "#00e5ff", "#ff2bd6", "#ffb800", "#8b5cf6", "#ff4d4d", "#ffffff"];
export const fileUrl = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

export const themeStyle = (theme) => {
  if (!theme?.frame && !theme?.accent) return undefined;
  const t = { ...DEFAULT, ...theme };
  return { "--tp-frame": t.frame, "--tp-accent": t.accent };
};

export const TokenBanner = ({ page, symbol }) => (!page?.banner_url && !page?.can_edit ? null :
  <div data-testid="token-banner" className={`relative mb-6 overflow-hidden rounded-2xl glass ${page?.banner_url ? "h-36 sm:h-52 lg:h-64" : "h-20"}`}>
    {page?.banner_url ? <img src={fileUrl(page.banner_url)} alt={`${symbol} project banner`} className="h-full w-full object-cover" data-testid="token-banner-image" />
      : <div className="h-full w-full bg-[radial-gradient(600px_200px_at_20%_0%,rgba(57,255,20,0.14),transparent_70%)]" />}
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10] via-transparent to-transparent" />
    {!page?.banner_url && page?.can_edit && <p className="absolute bottom-4 left-5 text-xs text-slate-400">Add a project banner from <b>Customize & admin</b>.</p>}
  </div>
);

const ColorField = ({ label, value, onChange, testid }) => (
  <div className="px-2 py-2">
    <p className="text-[11px] text-slate-400 mb-1.5">{label}</p>
    <div className="flex items-center gap-1.5 flex-wrap">
      {PRESETS.map((c) => <button key={c} type="button" aria-label={c} onClick={() => onChange(c)} style={{ background: c }} className={`h-5 w-5 rounded-full ring-1 ${value === c ? "ring-white ring-2" : "ring-white/20"}`} />)}
      <input data-testid={testid} type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-6 w-8 cursor-pointer rounded bg-transparent" />
      <span className="font-mono text-[10px] text-slate-500">{value}</span>
    </div>
  </div>
);

export const CustomizeMenu = ({ address, page, onChange, defaultOpen }) => {
  const [theme, setTheme] = useState({ ...DEFAULT, ...page.theme });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const uploadLogo = async (f) => {
    if (!f) return;
    const bad = await checkLogoFile(f);
    if (bad) return toast.error(bad);
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post(`/token-page/${address}/logo`, fd); onChange({ ...page, logo_url: r.data.logo_url }); toast.success("Logo updated"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const preview = (t) => { setTheme(t); onChange({ ...page, theme: t }); };
  const save = async () => {
    try { const r = await api.put(`/token-page/${address}/theme`, theme); onChange({ ...page, theme: r.data.theme }); toast.success("Page colors saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => {
    try { await api.put(`/token-page/${address}/theme`, {}); setTheme(DEFAULT); onChange({ ...page, theme: {} }); toast.success("Colors reset to HIVEVORA default"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Banner must be 5 MB or smaller");
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post(`/token-page/${address}/banner`, fd); onChange({ ...page, banner_url: r.data.banner_url }); toast.success("Banner updated"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const removeBanner = async () => {
    try { await api.delete(`/token-page/${address}/banner`); onChange({ ...page, banner_url: null }); toast.success("Banner removed"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <DropdownMenu defaultOpen={defaultOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="token-customize-button" className="flex items-center gap-1 rounded-full border border-lime/40 px-3 py-1 text-[11px] text-lime hover:bg-lime/10"><Palette className="h-3 w-3" /> Customize & admin</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-[#12161F] border-lime/20 text-slate-200" data-testid="token-customize-menu">
        <Link to={`/token/${address}/admin`} data-testid="token-admin-link" className="mx-1 my-1 flex items-center gap-2 rounded-lg border border-lime/30 bg-lime/5 px-3 py-2 text-xs font-semibold text-lime hover:bg-lime/10"><Trophy className="h-3.5 w-3.5" /> Admin · Competitions & fees</Link>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuLabel className="text-xs">Token logo</DropdownMenuLabel>
        <div className="flex items-center gap-2 px-2 pb-2">
          <input ref={logoRef} type="file" accept="image/png" hidden data-testid="token-logo-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
          {page.logo_url && <img src={fileUrl(page.logo_url)} alt="" className="h-8 w-8 rounded-full object-cover" />}
          <button data-testid="token-logo-upload" disabled={busy} onClick={() => logoRef.current?.click()} className="btn-lime px-3 py-1.5 text-[11px]"><ImagePlus className="h-3 w-3" /> {page.logo_url ? "Change logo" : "Upload logo"}</button>
        </div>
        <p className="px-2 pb-2 text-[10px] text-slate-500">Square image, at least 256×256.</p>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuLabel className="text-xs">Project banner</DropdownMenuLabel>
        <div className="flex gap-2 px-2 pb-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden data-testid="token-banner-input" onChange={(e) => upload(e.target.files?.[0])} />
          <button data-testid="token-banner-upload" disabled={busy} onClick={() => fileRef.current?.click()} className="btn-lime px-3 py-1.5 text-[11px]">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />} Upload banner</button>
          {page.banner_url && <button data-testid="token-banner-remove" onClick={removeBanner} className="flex items-center gap-1 rounded-lg border border-danger/40 px-2 text-[11px] text-danger"><Trash2 className="h-3 w-3" /> Remove</button>}
        </div>
        <p className="px-2 pb-2 text-[10px] text-slate-500">Wide image recommended (1500×500). PNG, JPEG, GIF or WebP, max 5 MB.</p>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuLabel className="text-xs">Card frame colors</DropdownMenuLabel>
        <ColorField label="Frame (card borders & glow)" value={theme.frame} onChange={(c) => preview({ ...theme, frame: c })} testid="token-theme-frame" />
        <ColorField label="Accent (card titles & icons)" value={theme.accent} onChange={(c) => preview({ ...theme, accent: c })} testid="token-theme-accent" />
        <div className="flex gap-2 px-2 py-2">
          <button data-testid="token-theme-save" onClick={save} className="btn-lime px-3 py-1.5 text-[11px]">Save colors</button>
          <button data-testid="token-theme-reset" onClick={reset} className="rounded-lg border border-white/15 px-3 text-[11px] text-slate-300">Reset</button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
